import React, { useState, useEffect, useRef } from 'react';
import { 
  Languages, Loader2, CheckCircle, ChevronRight, Award,
  Flag, FileText, Upload, Plus, Trash2, Eye, Check, X, Calendar, Download
} from 'lucide-react';
import { studentPanelAPI, studentCertificatesAPI } from '../../services/api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const LANGUAGES = {
  russian: { name: 'Rus tili', flag: '🇷🇺', color: '#dc2626' },
  english: { name: 'Ingliz tili', flag: '🇬🇧', color: '#2563eb' },
  german: { name: 'Nemis tili', flag: '🇩🇪', color: '#ca8a04' },
};

const StudentLanguageTest = () => {
  const [activeTab, setActiveTab] = useState('test'); // 'test' or 'certificates'
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState({});
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [testMode, setTestMode] = useState(false);
  const [questions, setQuestions] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [currentResult, setCurrentResult] = useState(null);
  
  // Sertifikatlar uchun state
  const [certificates, setCertificates] = useState([]);
  const [certLanguages, setCertLanguages] = useState([]);
  const [certLevels, setCertLevels] = useState([]);
  const [showCertModal, setShowCertModal] = useState(false);
  const [certForm, setCertForm] = useState({
    language: 'english',
    level: 'B1',
    certificate_name: '',
    issue_date: '',
    expiry_date: '',
    score: '',
  });
  const [certFile, setCertFile] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (activeTab === 'test') {
      fetchResults();
    } else {
      fetchCertificates();
    }
  }, [activeTab]);

  const fetchResults = async () => {
    setLoading(true);
    try {
      const response = await studentPanelAPI.getLanguageResults();
      setResults(response.data.results || {});
    } catch (error) {
      console.error('Natijalarni yuklashda xato:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCertificates = async () => {
    setLoading(true);
    try {
      const response = await studentCertificatesAPI.getAll();
      setCertificates(response.data.certificates || []);
      setCertLanguages(response.data.languages || []);
      setCertLevels(response.data.levels || []);
    } catch (error) {
      console.error('Sertifikatlarni yuklashda xato:', error);
    } finally {
      setLoading(false);
    }
  };

  const startTest = async (language) => {
    setSelectedLanguage(language);
    setLoading(true);
    try {
      const response = await studentPanelAPI.getLanguageTest(language);
      setQuestions(response.data.questions);
      setAnswers({});
      setTestMode(true);
      if (response.data.last_result) {
        setResults(prev => ({ ...prev, [language]: response.data.last_result }));
      }
    } catch (error) {
      console.error('Test yuklashda xato:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleSubmit = async () => {
    const totalQuestions = 
      (questions.fill_blank?.length || 0) + 
      (questions.choose_translation?.length || 0) + 
      (questions.write_translation?.length || 0);
    
    if (Object.keys(answers).length < totalQuestions) {
      alert(`Barcha savollarga javob bering! (${Object.keys(answers).length}/${totalQuestions})`);
      return;
    }

    setSubmitting(true);
    try {
      const response = await studentPanelAPI.submitLanguageTest(selectedLanguage, answers);
      setCurrentResult(response.data.result);
      setTestMode(false);
      fetchResults();
    } catch (error) {
      console.error('Yuborishda xato:', error);
      alert(error.response?.data?.error || 'Xatolik yuz berdi');
    } finally {
      setSubmitting(false);
    }
  };

  const goBack = () => {
    setSelectedLanguage(null);
    setTestMode(false);
    setQuestions(null);
    setCurrentResult(null);
    setAnswers({});
  };

  // Sertifikat funksiyalari
  const handleCertSubmit = async (e) => {
    e.preventDefault();
    if (!certFile) {
      alert('Sertifikat faylini yuklang!');
      return;
    }
    
    const formData = new FormData();
    formData.append('language', certForm.language);
    formData.append('level', certForm.level);
    formData.append('issue_date', certForm.issue_date);
    formData.append('certificate_file', certFile);
    
    if (certForm.certificate_name) formData.append('certificate_name', certForm.certificate_name);
    if (certForm.expiry_date) formData.append('expiry_date', certForm.expiry_date);
    if (certForm.score) formData.append('score', certForm.score);
    
    try {
      await studentCertificatesAPI.create(formData);
      setShowCertModal(false);
      resetCertForm();
      fetchCertificates();
    } catch (error) {
      alert(error.response?.data?.errors ? JSON.stringify(error.response.data.errors) : 'Xatolik yuz berdi');
    }
  };

  const deleteCertificate = async (id) => {
    if (window.confirm("Sertifikatni o'chirmoqchimisiz?")) {
      try {
        await studentCertificatesAPI.delete(id);
        fetchCertificates();
      } catch (error) {
        alert(error.response?.data?.error || 'Xatolik yuz berdi');
      }
    }
  };

  const resetCertForm = () => {
    setCertForm({
      language: 'english',
      level: 'B1',
      certificate_name: '',
      issue_date: '',
      expiry_date: '',
      score: '',
    });
    setCertFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const getFileUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${API_URL}${url}`;
  };

  if (loading && !testMode) {
    return (
      <div className="loading-container">
        <Loader2 className="spinner" size={40} />
        <p>Yuklanmoqda...</p>
      </div>
    );
  }

  // Natija ko'rsatish
  if (currentResult && !testMode) {
    const langInfo = LANGUAGES[selectedLanguage];
    return (
      <div className="page-container">
        <header className="page-header">
          <div className="page-title">
            <h1><Languages size={28} /> {langInfo.name} - Natija</h1>
          </div>
          <button className="btn btn-secondary" onClick={goBack}>
            ← Orqaga
          </button>
        </header>

        <div className="language-result-container">
          <div className="result-main-card" style={{ '--temp-color': langInfo.color }}>
            <div className="result-badge">
              <Award size={48} />
            </div>
            <div className="language-flag-large">{langInfo.flag}</div>
            <h2>{langInfo.name}</h2>
            <div className="result-type">{currentResult.level}</div>
            <div className="result-percentage">{currentResult.percentage}%</div>
            <p className="result-description">
              {currentResult.total_score} / {currentResult.total_questions} to'g'ri javob
            </p>
          </div>

          <div className="result-scores">
            <h3>Topshiriqlar bo'yicha natijalar:</h3>
            <div className="scores-grid">
              <div className="score-item">
                <div className="score-header">
                  <span className="score-name">Tushib qolgan so'z</span>
                  <span className="score-value">{currentResult.fill_blank_score}/{currentResult.fill_blank_total}</span>
                </div>
                <div className="score-bar">
                  <div className="score-fill" style={{ 
                    width: `${(currentResult.fill_blank_score/currentResult.fill_blank_total)*100}%`,
                    background: langInfo.color 
                  }} />
                </div>
              </div>
              <div className="score-item">
                <div className="score-header">
                  <span className="score-name">Tarjimani tanlash</span>
                  <span className="score-value">{currentResult.choose_score}/{currentResult.choose_total}</span>
                </div>
                <div className="score-bar">
                  <div className="score-fill" style={{ 
                    width: `${(currentResult.choose_score/currentResult.choose_total)*100}%`,
                    background: langInfo.color 
                  }} />
                </div>
              </div>
              <div className="score-item">
                <div className="score-header">
                  <span className="score-name">Tarjima yozish</span>
                  <span className="score-value">{currentResult.write_score}/{currentResult.write_total}</span>
                </div>
                <div className="score-bar">
                  <div className="score-fill" style={{ 
                    width: `${(currentResult.write_score/currentResult.write_total)*100}%`,
                    background: langInfo.color 
                  }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Test rejimi
  if (testMode && questions) {
    const langInfo = LANGUAGES[selectedLanguage];
    const totalQuestions = 
      (questions.fill_blank?.length || 0) + 
      (questions.choose_translation?.length || 0) + 
      (questions.write_translation?.length || 0);
    const answeredCount = Object.keys(answers).length;
    const progress = (answeredCount / totalQuestions) * 100;

    return (
      <div className="page-container">
        <header className="page-header">
          <div className="page-title">
            <h1>{langInfo.flag} {langInfo.name}</h1>
            <p>{totalQuestions} ta savol</p>
          </div>
          <button className="btn btn-secondary" onClick={goBack}>
            ← Bekor qilish
          </button>
        </header>

        {/* Progress */}
        <div className="test-progress-card">
          <div className="progress-info">
            <span>Jarayon: {answeredCount} / {totalQuestions}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progress}%`, background: langInfo.color }} />
          </div>
        </div>

        {/* 1. Tushib qolgan so'z */}
        {questions.fill_blank?.length > 0 && (
          <div className="test-section">
            <h3 className="test-section-title">
              <span className="section-number">1</span>
              Tushib qolgan so'zni toping
            </h3>
            <div className="questions-container">
              {questions.fill_blank.map((q, idx) => (
                <div key={q.id} className={`question-card ${answers[q.id] ? 'answered' : ''}`}>
                  <div className="question-number">{idx + 1}</div>
                  <div className="question-content">
                    <p className="question-text-lang">{q.question_text}</p>
                    <div className="options-grid">
                      {['a', 'b', 'c', 'd'].map(opt => (
                        <button
                          key={opt}
                          className={`option-btn ${answers[q.id] === q[`option_${opt}`] ? 'selected' : ''}`}
                          onClick={() => handleAnswer(q.id, q[`option_${opt}`])}
                        >
                          <span className="option-letter">{opt.toUpperCase()}</span>
                          {q[`option_${opt}`]}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 2. Tarjimani tanlash */}
        {questions.choose_translation?.length > 0 && (
          <div className="test-section">
            <h3 className="test-section-title">
              <span className="section-number">2</span>
              So'zning tarjimasini tanlang
            </h3>
            <div className="questions-container">
              {questions.choose_translation.map((q, idx) => (
                <div key={q.id} className={`question-card ${answers[q.id] ? 'answered' : ''}`}>
                  <div className="question-number">{idx + 1}</div>
                  <div className="question-content">
                    <p className="question-text-lang highlight">{q.question_text}</p>
                    <div className="options-grid">
                      {['a', 'b', 'c', 'd'].map(opt => (
                        <button
                          key={opt}
                          className={`option-btn ${answers[q.id] === q[`option_${opt}`] ? 'selected' : ''}`}
                          onClick={() => handleAnswer(q.id, q[`option_${opt}`])}
                        >
                          <span className="option-letter">{opt.toUpperCase()}</span>
                          {q[`option_${opt}`]}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 3. Tarjima yozish */}
        {questions.write_translation?.length > 0 && (
          <div className="test-section">
            <h3 className="test-section-title">
              <span className="section-number">3</span>
              So'zning tarjimasini yozing
            </h3>
            <div className="questions-container">
              {questions.write_translation.map((q, idx) => (
                <div key={q.id} className={`question-card ${answers[q.id] ? 'answered' : ''}`}>
                  <div className="question-number">{idx + 1}</div>
                  <div className="question-content">
                    <p className="question-text-lang highlight">{q.question_text}</p>
                    <input
                      type="text"
                      className="write-answer-input"
                      placeholder="Tarjimasini yozing..."
                      value={answers[q.id] || ''}
                      onChange={(e) => handleAnswer(q.id, e.target.value)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Submit */}
        <div className="test-submit-section">
          <button 
            className="btn btn-success btn-lg"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? <Loader2 className="spinner" size={20} /> : <CheckCircle size={20} />}
            Testni yakunlash
          </button>
        </div>
      </div>
    );
  }

  // Asosiy sahifa - Tablar bilan
  return (
    <div className="page-container">
      <header className="page-header">
        <div className="page-title">
          <h1><Languages size={28} /> Til bilish</h1>
          <p>Test topshiring yoki sertifikat yuklang</p>
        </div>
      </header>

      {/* Tabs */}
      <div className="tabs-container">
        <button 
          className={`tab-btn ${activeTab === 'test' ? 'active' : ''}`}
          onClick={() => setActiveTab('test')}
        >
          <Languages size={18} />
          Til testi
        </button>
        <button 
          className={`tab-btn ${activeTab === 'certificates' ? 'active' : ''}`}
          onClick={() => setActiveTab('certificates')}
        >
          <FileText size={18} />
          Sertifikatlarim
        </button>
      </div>

      {/* Test Tab */}
      {activeTab === 'test' && (
        <div className="language-cards-grid">
          {Object.entries(LANGUAGES).map(([key, lang]) => {
            const result = results[key];
            const hasResult = !!result;
            
            return (
              <div key={key} className="language-card" style={{ '--lang-color': lang.color }}>
                <div className="language-flag">{lang.flag}</div>
                <h3>{lang.name}</h3>
                
                {hasResult && (
                  <div className="language-last-result">
                    <div className="result-level">{result.level}</div>
                    <div className="result-score">{result.percentage}%</div>
                    <p>{result.total_score}/{result.total_questions} to'g'ri</p>
                  </div>
                )}
                
                <div className="language-actions">
                  {hasResult && (
                    <button 
                      className="btn btn-secondary"
                      onClick={() => { setSelectedLanguage(key); setCurrentResult(result); }}
                    >
                      Natijani ko'rish
                    </button>
                  )}
                  <button 
                    className="btn btn-primary"
                    onClick={() => startTest(key)}
                  >
                    {hasResult ? 'Qayta topshirish' : 'Testni boshlash'}
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Certificates Tab */}
      {activeTab === 'certificates' && (
        <div className="certificates-section">
          <div className="section-header">
            <h3>Mening sertifikatlarim</h3>
            <button className="btn btn-primary" onClick={() => setShowCertModal(true)}>
              <Plus size={18} />
              Sertifikat yuklash
            </button>
          </div>

          {certificates.length === 0 ? (
            <div className="empty-state">
              <FileText size={48} />
              <p>Hali sertifikat yuklanmagan</p>
              <button className="btn btn-primary" onClick={() => setShowCertModal(true)}>
                <Upload size={18} />
                Birinchi sertifikatni yuklash
              </button>
            </div>
          ) : (
            <div className="certificates-grid">
              {certificates.map(cert => (
                <div key={cert.id} className={`certificate-card ${cert.is_verified ? 'verified' : 'pending'}`}>
                  <div className="cert-header">
                    <div className="cert-language">
                      <span className="cert-lang-name">{cert.language_display}</span>
                      <span className="cert-level">{cert.level}</span>
                    </div>
                    <div className={`cert-status ${cert.is_verified ? 'verified' : 'pending'}`}>
                      {cert.is_verified ? (
                        <><Check size={14} /> Tasdiqlangan</>
                      ) : (
                        <><Eye size={14} /> Tekshiruvda</>
                      )}
                    </div>
                  </div>
                  
                  <div className="cert-body">
                    {cert.certificate_name && (
                      <div className="cert-name">{cert.certificate_name}</div>
                    )}
                    {cert.score && (
                      <div className="cert-score">Ball: {cert.score}</div>
                    )}
                    <div className="cert-date">
                      <Calendar size={14} />
                      {new Date(cert.issue_date).toLocaleDateString('uz-UZ')}
                      {cert.expiry_date && ` - ${new Date(cert.expiry_date).toLocaleDateString('uz-UZ')}`}
                    </div>
                  </div>
                  
                  <div className="cert-actions">
                    <a 
                      href={getFileUrl(cert.certificate_file)} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="btn btn-sm"
                    >
                      <Download size={16} />
                      Ko'rish
                    </a>
                    {!cert.is_verified && (
                      <button 
                        className="btn btn-sm btn-danger"
                        onClick={() => deleteCertificate(cert.id)}
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Certificate Modal */}
      {showCertModal && (
        <div className="modal-overlay" onClick={() => setShowCertModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Sertifikat yuklash</h2>
              <button className="modal-close" onClick={() => setShowCertModal(false)}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCertSubmit}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label>Til *</label>
                    <select
                      value={certForm.language}
                      onChange={(e) => setCertForm({...certForm, language: e.target.value})}
                      required
                    >
                      {certLanguages.map(l => (
                        <option key={l.value} value={l.value}>{l.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Daraja *</label>
                    <select
                      value={certForm.level}
                      onChange={(e) => setCertForm({...certForm, level: e.target.value})}
                      required
                    >
                      {certLevels.map(l => (
                        <option key={l.value} value={l.value}>{l.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Sertifikat nomi</label>
                  <input
                    type="text"
                    value={certForm.certificate_name}
                    onChange={(e) => setCertForm({...certForm, certificate_name: e.target.value})}
                    placeholder="IELTS, TOEFL, CEFR va h.k."
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Berilgan sana *</label>
                    <input
                      type="date"
                      value={certForm.issue_date}
                      onChange={(e) => setCertForm({...certForm, issue_date: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Amal qilish muddati</label>
                    <input
                      type="date"
                      value={certForm.expiry_date}
                      onChange={(e) => setCertForm({...certForm, expiry_date: e.target.value})}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Ball</label>
                  <input
                    type="text"
                    value={certForm.score}
                    onChange={(e) => setCertForm({...certForm, score: e.target.value})}
                    placeholder="Masalan: 6.5, 90, B2"
                  />
                </div>

                <div className="form-group">
                  <label>Sertifikat fayli (PDF) *</label>
                  <div className="file-upload-zone">
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept=".pdf"
                      onChange={(e) => setCertFile(e.target.files[0])}
                      required
                    />
                    {certFile && (
                      <div className="file-selected">
                        <FileText size={20} />
                        {certFile.name}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCertModal(false)}>
                  Bekor qilish
                </button>
                <button type="submit" className="btn btn-primary">
                  <Upload size={18} />
                  Yuklash
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentLanguageTest;
